import React, { useState, useEffect } from "react";
import { Box, TextField, IconButton, Typography } from "@mui/material";
import { Modal, Button } from "../../../design-system";
import { CloseIcon } from "../../../design-system/icons";
import { colors } from "../../../design-system/colors";
import {
  RENAME_DIALOG_TITLE,
  RENAME_DIALOG_PLACEHOLDER_NAME,
  RENAME_DIALOG_PLACEHOLDER_DESCRIPTION,
  BUTTON_CANCEL,
  BUTTON_SAVE,
} from "../constants/myLibraryText";

interface RenameDialogProps {
  open: boolean;
  currentTitle: string;
  currentDescription?: string;
  onClose: () => void;
  onConfirm: (newTitle: string, newDescription?: string) => void;
  loading?: boolean;
}

const inputSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: colors.background.search,
    color: colors.neutral.white,
    borderRadius: "8px",
    "& fieldset": { borderColor: colors.background[600] },
    "&:hover fieldset": { borderColor: colors.background[500] },
    "&.Mui-focused fieldset": { borderColor: "transparent" },
    "& input::placeholder, & textarea::placeholder": {
      color: colors.text[700],
      opacity: 1,
    },
  },
};

const RenameDialog: React.FC<RenameDialogProps> = ({
  open,
  currentTitle,
  currentDescription,
  onClose,
  onConfirm,
  loading = false,
}): JSX.Element => {
  const [title, setTitle] = useState<string>(currentTitle);
  const [description, setDescription] = useState<string>(
    currentDescription ?? "",
  );
  const [titleError, setTitleError] = useState<string>("");

  const showDescription = currentDescription !== undefined;

  useEffect(() => {
    if (open) {
      setTitle(currentTitle);
      setDescription(currentDescription ?? "");
      setTitleError("");
    }
  }, [open, currentTitle, currentDescription]);

  const handleTitleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const newTitle = event.target.value;
    setTitle(newTitle);
    if (newTitle.trim().length === 0) {
      setTitleError("Title cannot be empty");
    } else if (newTitle.length > 200) {
      setTitleError("Title cannot exceed 200 characters");
    } else {
      setTitleError("");
    }
  };

  const handleDescriptionChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setDescription(event.target.value);
  };

  const handleConfirm = (): void => {
    if (!titleError && title.trim().length > 0) {
      onConfirm(title.trim(), showDescription ? description : undefined);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent): void => {
    if (
      event.key === "Enter" &&
      event.ctrlKey &&
      !titleError &&
      title.trim().length > 0
    ) {
      handleConfirm();
    }
  };

  return (
    <Modal open={open} onClose={onClose} variant="compact" keepMounted>
      <Box
        sx={{
          padding: "24px",
          display: "flex",
          flexDirection: "column",
          gap: "24px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              color: colors.neutral.white,
              fontFamily: "Outfit, sans-serif",
              fontSize: "18px",
              fontWeight: 600,
            }}
          >
            {RENAME_DIALOG_TITLE}
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: colors.neutral[400],
              "&:hover": { backgroundColor: colors.background[700] },
            }}
            aria-label="Close"
          >
            <CloseIcon size={20} />
          </IconButton>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <TextField
            autoFocus
            fullWidth
            placeholder={RENAME_DIALOG_PLACEHOLDER_NAME}
            value={title}
            onChange={handleTitleChange}
            onKeyPress={handleKeyPress}
            error={!!titleError}
            helperText={titleError}
            variant="outlined"
            sx={{
              ...inputSx,
              "& .MuiFormHelperText-root": {
                color: titleError ? colors.error.main : "transparent",
                marginTop: "4px",
                fontSize: "12px",
              },
            }}
          />
          {showDescription && (
            <TextField
              fullWidth
              placeholder={RENAME_DIALOG_PLACEHOLDER_DESCRIPTION}
              value={description}
              onChange={handleDescriptionChange}
              variant="outlined"
              multiline
              minRows={2}
              sx={inputSx}
            />
          )}
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
          <Button
            variant="text"
            onClick={onClose}
            sx={{
              borderRadius: "100px",
              "&:hover": { backgroundColor: colors.background[700] },
            }}
          >
            {BUTTON_CANCEL}
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={!!titleError || title.trim().length === 0}
            loading={loading}
          >
            {BUTTON_SAVE}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default RenameDialog;
